import { forwardRef, useRef, useState } from 'react';
import { Pencil } from 'lucide-react';
import { ScrollArea } from '~/primitives/scroll-area';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/primitives/tooltip';
import { cn, formatLevelGDID, primaryClickHandler } from '~/util';
import { PackLevel, PackTier as PackTierType } from '~/util/types/packs';
import { getRouteApi, Link, useParams } from '@tanstack/react-router';
import PackProgress from './-PackProgress';
import { useScopes } from '~/components/Providers/AuthProvider';
import { EditPackTierSheet } from './-EditPackTierSheet';
import { useAutoScrollIntoView } from '~/util/hooks/useAutoScrollIntoView';
import { useElementMap } from '~/util/hooks/useElementMap';

const Route = getRouteApi('/packs');

const PackTier = forwardRef<
    HTMLDivElement,
    { tier: PackTierType; onPackSelect?: () => void }
>(({ tier, onPackSelect }, ref) => {
    const { packid } = useParams({ strict: false });
    const search = Route.useSearch({ select: ({ search }) => search });

    const [isEditOpen, setIsEditOpen] = useState(false);

    const { hasScope } = useScopes();

    const viewportRef = useRef<HTMLDivElement>(null);

    const { getElement: getPackElement, registerElement: registerPackElement } =
        useElementMap<string, HTMLAnchorElement>();

    const selectedPackIsInTier = tier.packs.some((pack) => pack.id === packid);

    useAutoScrollIntoView({
        viewportRef,
        enabled: !!packid && selectedPackIsInTier,
        scrollKey: `${packid}|${search}`,
        getTarget: () => (packid ? getPackElement(packid) : null),
    });

    return (
        <div ref={ref} className="gap-3 bg-background-dark rounded-xl ">
            <div
                style={{ background: tier.color }}
                className="flex justify-between w-full p-2 rounded-t-xl"
            >
                <h2 className="text-shadow-3">{tier?.name}</h2>
                {hasScope('pack_tier_modify') && (
                    <div className="h-8 w-8 relative inline-block  transition-transform align-middle">
                        <div
                            className="absolute text-black blur-sm z-0"
                            aria-hidden="true"
                        >
                            <Pencil className="h-8 w-8" />
                        </div>
                        <div className="relative z-10">
                            <Pencil
                                className="absolute top-0 left-0 h-8 w-8 hover:text-secondary-light transition-colors cursor-pointer"
                                onClick={() => {
                                    setIsEditOpen(true);
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>

            <ScrollArea
                className="my-2 pr-1 h-[60vh] lg:h-[40vh]"
                viewportRef={viewportRef}
            >
                <div className="flex flex-col gap-2">
                    {tier.packs.map((pack) => (
                        <Tooltip key={pack.id}>
                            <TooltipTrigger asChild>
                                <Link
                                    ref={registerPackElement(pack.id)}
                                    className={cn(
                                        'transition-all bg-background-dark hover:bg-background-light p-2 cursor-pointer rounded-lg text-left',
                                        pack.id === packid &&
                                            'bg-primary hover:bg-primary text-primary-foreground hover:text-primary-foreground',
                                    )}
                                    from="/packs/$packid"
                                    params={{
                                        packid: pack.id,
                                    }}
                                    search={(prev) => prev}
                                    resetScroll={false}
                                    onClick={primaryClickHandler(onPackSelect)}
                                >
                                    <div className="flex justify-between">
                                        <p className="text-xl font-semibold">
                                            {pack.name}
                                        </p>
                                        <p>
                                            {pack.levels.reduce(
                                                (
                                                    acc: number,
                                                    level: PackLevel,
                                                ) => {
                                                    if (
                                                        level.completed_by_user
                                                    ) {
                                                        return acc + 1;
                                                    }
                                                    return acc;
                                                },
                                                0,
                                            )}
                                            /{pack.levels.length}
                                        </p>
                                    </div>
                                    <PackProgress
                                        pack={pack}
                                        selected={pack.id === packid}
                                        className="h-2 w-full"
                                    />
                                </Link>
                            </TooltipTrigger>
                            <TooltipContent
                                side="right"
                                className="px-3 bg-background-light border-none"
                            >
                                <h3 className="text-xl font-semibold my-2">
                                    {pack.name}
                                </h3>
                                <ul className="flex flex-col gap-1">
                                    {pack.levels
                                        .sort(
                                            (a, b) =>
                                                (a.position ?? Infinity) -
                                                (b.position ?? Infinity),
                                        )
                                        .map((level) => (
                                            <li
                                                className={cn(
                                                    'group bg-background-dark hover:bg-primary hover:text-primary-foreground transition-all p-2 rounded-lg pointer-cursor',
                                                    level.completed_by_user &&
                                                        'bg-confirm/30 hover:bg-confirm/80',
                                                )}
                                                key={level.id}
                                            >
                                                <Link
                                                    className="flex gap-2 items-center justify-center "
                                                    to="/list/$levelid"
                                                    params={{
                                                        levelid:
                                                            formatLevelGDID(
                                                                level,
                                                            ),
                                                    }}
                                                >
                                                    <span
                                                        className={cn(
                                                            !level.completed_by_user
                                                                ? 'text-secondary-light'
                                                                : 'text-secondary-foreground',
                                                            'group-hover:text-secondary-foreground transition-colors',
                                                        )}
                                                    >
                                                        #{level.position}
                                                    </span>
                                                    <p className="grow">
                                                        {level.name}
                                                    </p>
                                                </Link>
                                            </li>
                                        ))}
                                </ul>
                            </TooltipContent>
                        </Tooltip>
                    ))}
                </div>
            </ScrollArea>
            {hasScope('pack_tier_modify') && (
                <EditPackTierSheet
                    open={isEditOpen}
                    onOpenChange={setIsEditOpen}
                    packtier={tier}
                />
            )}
        </div>
    );
});

PackTier.displayName = 'PackTier';

export default PackTier;
